// Shared header-pattern matching for the ingestion walkers.
//
// Both chunk-size-estimator.ts (annotation time) and chunker.ts
// (ingestion time) walk raw extracted text against literal header
// patterns from the proposed BookStructure. This module centralises
// two things that used to be duplicated between them, both of which
// had latent bugs:
//
// 1. **Unicode normalisation before matching.** Extracted text and
//    Olivier's header patterns don't always agree on quote form
//    (`'` vs `’`), space form (regular vs non-breaking), etc.
//    Without normalisation, any pattern containing a French elision
//    like `d'IA` or `l'ecosysteme` silently fails to match — the
//    annotator leaves the token estimate undefined and the chunker
//    throws at ingestion time.
//
// 2. **TOC-first-match bug in chapter offsets.** For a typical book
//    with a table of contents, EVERY chapter's header regex matches
//    at TWO places: the TOC entry (near the top of the doc) and the
//    actual chapter heading (later in the doc). Naive "first match"
//    picks the TOC entry — the "span" between consecutive chapters
//    is then a few lines of TOC, not the actual chapter content.
//    Ch1..ChN-1 all end up tiny; ChN swallows the whole book.
//
//    Heuristic fix: for each chapter, take the LAST occurrence of
//    its pattern in the text. Chapter titles appear only twice in
//    a standard book layout (TOC + real heading), and the real
//    heading is always the later of the two.
//
//    Same shape at the sub-section level: many chapters open with an
//    intro enumeration ("Dans ce chapitre : 2.1 …, 2.2 …, 2.3 …"),
//    which is INSIDE the chapter slice — so scoping to the chapter
//    doesn't save us. Sub-sections use findLastMatch too. See
//    chunker.ts + chunk-size-estimator.ts for the walk sites.
//
// Unicode escapes below (\uXXXX) rather than literal characters —
// several of them (NBSP, zero-width joiners) trip the eslint
// `no-irregular-whitespace` rule when inlined.

// U+2018 LEFT SINGLE QUOTATION MARK, U+2019 RIGHT SINGLE QUOTATION MARK,
// U+201B SINGLE HIGH-REVERSED-9 QUOTATION MARK
const CURLY_SINGLE_QUOTES = /[‘’‛]/g;
// U+201C LEFT DOUBLE QUOTATION MARK, U+201D RIGHT DOUBLE, U+201F DOUBLE HIGH-REVERSED
const CURLY_DOUBLE_QUOTES = /[“”‟]/g;
// U+00A0 NO-BREAK SPACE
const NBSP = /\u00A0/g;
// U+200B ZWSP, U+200C ZWNJ, U+200D ZWJ, U+FEFF BOM
// Alternation rather than a character class \u2014 ESLint's
// `no-misleading-character-class` flags ZWJ (\u200D) inside a class
// because it can join adjacent chars into a compound grapheme.
// Alternation avoids the warning; runtime behaviour is identical.
const ZERO_WIDTH = /\u200B|\u200C|\u200D|\uFEFF/g;

/**
 * Normalise text for regex matching:
 *   - NFC unicode form (combining marks collapsed)
 *   - Curly quotes -> straight equivalents
 *   - Non-breaking spaces -> regular spaces
 *   - Zero-width joiners / BOM stripped
 *
 * Applied to BOTH the source text and the pattern before matching so
 * cosmetic differences between the extraction and Olivier-authored
 * patterns don't kill the match.
 */
export function normalizeForMatch(input: string): string {
  return input
    .normalize('NFC')
    .replace(CURLY_SINGLE_QUOTES, "'")
    .replace(CURLY_DOUBLE_QUOTES, '"')
    .replace(NBSP, ' ')
    .replace(ZERO_WIDTH, '');
}

/**
 * Compile a literal header pattern (as authored by Olivier / Sonnet)
 * into a case-insensitive, multiline-anchored, whitespace-tolerant
 * regex ready to run against normalised text.
 */
export function compileHeaderRegex(headerPatternLiteral: string): RegExp {
  const escaped = normalizeForMatch(headerPatternLiteral)
    .trim()
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/\s+/g, '\\s+');
  return new RegExp('^' + escaped, 'gim');
}

/**
 * Find every match of `headerPatternLiteral` in `text`. Returns their
 * offsets in the normalised text — the normalisation transformations we
 * apply are all single-char -> single-char (curly quotes, NBSP) or
 * single-char -> empty (zero-width chars). Since zero-width chars are
 * exceedingly rare in book prose, the offset drift vs. the original
 * text is bounded and callers can slice by the returned offsets without
 * detectable drift.
 *
 * Empty array if no matches.
 */
export function findAllMatches(text: string, headerPatternLiteral: string): number[] {
  const normalizedText = normalizeForMatch(text);
  const regex = compileHeaderRegex(headerPatternLiteral);
  const offsets: number[] = [];
  let m: RegExpExecArray | null;
  while ((m = regex.exec(normalizedText)) !== null) {
    offsets.push(m.index);
    // Guard against zero-width matches spinning forever.
    if (m.index === regex.lastIndex) regex.lastIndex += 1;
  }
  return offsets;
}

/**
 * Last match — the TOC-skip heuristic. Standard book layouts mention
 * each chapter twice (TOC + real heading), and each chapter often
 * opens with an intro enumeration listing its sub-sections; in both
 * cases the real heading is the later occurrence.
 */
export function findLastMatch(text: string, headerPatternLiteral: string): number | null {
  const offsets = findAllMatches(text, headerPatternLiteral);
  return offsets.length > 0 ? offsets[offsets.length - 1]! : null;
}
